import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Dialog, LinearProgress, Stack, Typography } from '@mui/material';
import { LearningMaterial, LearningMaterialSection, Media } from '../../services/learningMaterialService';

interface LearningMaterialContentProps {
  material: {
    content: LearningMaterial['content'];
    sections?: LearningMaterialSection[];
    materialMedia?: Array<{
      mediaId: number;
      order?: number;
      placement?: string;
      caption?: string;
      width?: string;
      rotation?: number;
      media: Media;
    }>;
  };
  defaultView?: 'full' | 'sectioned';
  allowViewToggle?: boolean;
  allowHide?: boolean;
}

type RenderableAttachment = NonNullable<LearningMaterialContentProps['material']['materialMedia']>[number];

interface RenderableSection {
  id: string;
  title?: string | null;
  content: string;
  attachments: RenderableAttachment[];
}

const LearningMaterialContent: React.FC<LearningMaterialContentProps> = ({
  material,
  defaultView = 'full',
  allowViewToggle = false,
  allowHide = false,
}) => {
  const normalizedSections = useMemo<RenderableSection[]>(() => {
    if (material.sections && material.sections.length > 0) {
      return material.sections
        .slice()
        .sort((left, right) => (left.order || 0) - (right.order || 0))
        .map((section, index) => ({
          id: String(section.id || index + 1),
          title: section.title,
          content: section.content || '',
          attachments: (section.sectionMedia || [])
            .slice()
            .sort((left, right) => (left.order || 0) - (right.order || 0)),
        }));
    }

    return [{
      id: 'legacy-material',
      title: null,
      content: material.content,
      attachments: (material.materialMedia || []).slice(),
    }];
  }, [material.content, material.materialMedia, material.sections]);

  const [viewMode, setViewMode] = useState<'full' | 'sectioned'>(defaultView);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isHidden, setIsHidden] = useState(false);
  const [expandedAttachment, setExpandedAttachment] = useState<RenderableAttachment | null>(null);

  useEffect(() => {
    setViewMode(defaultView);
  }, [defaultView]);

  useEffect(() => {
    setCurrentSectionIndex((previousIndex) => {
      if (normalizedSections.length === 0) {
        return 0;
      }

      return Math.min(previousIndex, normalizedSections.length - 1);
    });
  }, [normalizedSections.length]);

  const getAttachmentDisplayWidth = (attachment: RenderableAttachment) => {
    const normalizedWidth = attachment.width?.trim();

    if (normalizedWidth && normalizedWidth !== '100%') {
      return normalizedWidth;
    }

    if (typeof attachment.media.width === 'number' && attachment.media.width > 0) {
      return `${Math.round(attachment.media.width / 2)}px`;
    }

    return '50%';
  };

  const renderSectionImages = (attachments: RenderableAttachment[]) => {
    const separateImages = attachments
    .filter((attachment) => attachment?.media?.fileType === 'image')
    .sort((left, right) => (left.order || 0) - (right.order || 0));

  const topImages = separateImages.filter((attachment) => attachment.placement === 'top');
  const bottomImages = separateImages.filter((attachment) => attachment.placement === 'bottom');
  const leftImages = separateImages.filter((attachment) => attachment.placement === 'left');
  const rightImages = separateImages.filter((attachment) => attachment.placement === 'right');
  const galleryImages = separateImages.filter((attachment) => {
    return !attachment.placement || attachment.placement === 'gallery' || attachment.placement === 'inline';
  });

    const renderImageCard = (attachment: RenderableAttachment, variant: 'stack' | 'gallery') => {
    const isSidePlacement = attachment.placement === 'left' || attachment.placement === 'right';
    const displayWidth = isSidePlacement && (!attachment.width || attachment.width === '100%')
      ? '280px'
      : getAttachmentDisplayWidth(attachment);

    return (
      <Box
        key={attachment.mediaId}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          backgroundColor: 'background.paper',
          width: 'fit-content',
          maxWidth: '100%',
        }}
      >
        <Box
          component="img"
          src={attachment.media.blobUrl}
          alt={attachment.media.altText || attachment.caption || 'Materiaalikuva'}
          onClick={() => setExpandedAttachment(attachment)}
          sx={{
            width: displayWidth,
            maxWidth: '100%',
            display: 'block',
            margin: '0 auto',
            borderRadius: 1,
            transform: `rotate(${attachment.rotation || 0}deg)`,
            transformOrigin: 'center center',
            cursor: 'zoom-in',
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Klikkaa kuva suuremmaksi
        </Typography>
        {attachment.caption && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {attachment.caption}
          </Typography>
        )}
      </Box>
    );
    };

    const renderStack = (stackAttachments: RenderableAttachment[]) => {
    if (!stackAttachments.length) {
      return null;
    }

    return (
      <Box sx={{ display: 'grid', gap: 2, alignContent: 'start', justifyItems: 'start', mb: 3 }}>
        {stackAttachments.map((attachment) => renderImageCard(attachment, 'stack'))}
      </Box>
    );
  };

    return {
      topImages,
      bottomImages,
      leftImages,
      rightImages,
      galleryImages,
      renderImageCard,
      renderStack,
    };
  };

  const renderSection = (section: RenderableSection) => {
    const {
      topImages,
      bottomImages,
      leftImages,
      rightImages,
      galleryImages,
      renderImageCard,
      renderStack,
    } = renderSectionImages(section.attachments);

    return (
      <Box key={section.id} sx={{ mb: 3 }}>
        {section.title && (
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            {section.title}
          </Typography>
        )}

        {renderStack(topImages)}

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {leftImages.length > 0 && (
            <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' }, maxWidth: '100%' }}>
              {renderStack(leftImages)}
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }} dangerouslySetInnerHTML={{ __html: section.content }} />

          {rightImages.length > 0 && (
            <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' }, maxWidth: '100%' }}>
              {renderStack(rightImages)}
            </Box>
          )}
        </Box>

        {renderStack(bottomImages)}

        {galleryImages.length > 0 && (
          <Box sx={{ mt: 3, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
            {galleryImages.map((attachment) => renderImageCard(attachment, 'gallery'))}
          </Box>
        )}
      </Box>
    );
  };

  if (!normalizedSections.length) {
    return null;
  }

  const currentSection = normalizedSections[currentSectionIndex];
  const progressValue = normalizedSections.length > 0
    ? ((currentSectionIndex + 1) / normalizedSections.length) * 100
    : 0;

  return (
    <Box>
      {(allowViewToggle || allowHide) && normalizedSections.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap sx={{ mb: 2 }}>
          {allowViewToggle && normalizedSections.length > 1 && (
            <>
              <Button
                variant={viewMode === 'sectioned' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('sectioned')}
              >
                Näytä osioittain
              </Button>
              <Button
                variant={viewMode === 'full' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('full')}
              >
                Näytä koko materiaali
              </Button>
            </>
          )}
          {allowHide && (
            <Button variant="text" onClick={() => setIsHidden((previousValue) => !previousValue)}>
              {isHidden ? 'Näytä materiaali' : 'Piilota materiaali'}
            </Button>
          )}
        </Stack>
      )}

      {isHidden ? (
        <Chip label="Materiaali on piilotettu" variant="outlined" />
      ) : viewMode === 'sectioned' && normalizedSections.length > 1 ? (
        <Box>
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="subtitle2">
                Osio {currentSectionIndex + 1} / {normalizedSections.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round(progressValue)} % materiaalista
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressValue} sx={{ height: 8, borderRadius: 999 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${normalizedSections.length}, minmax(0, 1fr))`, gap: 0.75, mt: 1.25 }}>
              {normalizedSections.map((section, index) => (
                <Box
                  key={section.id}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: index === currentSectionIndex
                      ? 'primary.main'
                      : index < currentSectionIndex
                        ? 'primary.light'
                        : 'divider',
                  }}
                />
              ))}
            </Box>
          </Box>

          {renderSection(currentSection)}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
            <Button
              variant="outlined"
              onClick={() => setCurrentSectionIndex((currentValue) => Math.max(0, currentValue - 1))}
              disabled={currentSectionIndex === 0}
            >
              Edellinen osio
            </Button>
            <Button
              variant="contained"
              onClick={() => setCurrentSectionIndex((currentValue) => Math.min(normalizedSections.length - 1, currentValue + 1))}
              disabled={currentSectionIndex === normalizedSections.length - 1}
            >
              Seuraava osio
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box>
          {normalizedSections.map((section) => renderSection(section))}
        </Box>
      )}

      <Dialog
        open={Boolean(expandedAttachment)}
        onClose={() => setExpandedAttachment(null)}
        maxWidth="xl"
      >
        {expandedAttachment && (
          <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
            <Box
              component="img"
              src={expandedAttachment.media.blobUrl}
              alt={expandedAttachment.media.altText || expandedAttachment.caption || 'Materiaalikuva'}
              sx={{
                display: 'block',
                maxWidth: 'min(90vw, 1200px)',
                maxHeight: '85vh',
                width: 'auto',
                height: 'auto',
                margin: '0 auto',
                transform: `rotate(${expandedAttachment.rotation || 0}deg)`,
                transformOrigin: 'center center',
              }}
            />
            {expandedAttachment.caption && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
                {expandedAttachment.caption}
              </Typography>
            )}
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default LearningMaterialContent;